// src/views/adminView/contractsView/RenewContractModal.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Modal "Renovar contrato": se adjunta el PDF firmado, se fijan la firma y la vigencia del
// documento y, si el contrato no está activo, se elige si se reactiva.
//
// El PDF va al S3 de la plataforma por el proxy de archivos del BFF; contracts verifica el
// objeto (host, prefijo, firma `%PDF-`) y guarda solo la referencia en el historial. El
// envío es: validar → sha256 del archivo → subir (un reintento con el mismo archivo reusa la
// subida) → POST renewal. Si la respuesta no llega (red, timeout, proxy) la renovación pudo
// guardarse igual: se relee el contrato (y otra vez a los ~4 s si todavía no aparece) y, si su
// entrada más reciente es este sha256 con estas fechas y es de este envío, cuenta como éxito;
// si no, el reintento es seguro porque contracts deduplica contra esa entrada más reciente. Si
// la renovación falla de forma definitiva, el objeto subido queda huérfano en S3 (aceptado).
//
// El padre lo monta al abrirlo y lo desmonta al cerrarlo: el estado inicial sale del
// contrato de ese momento.
// ─────────────────────────────────────────────────────────────────────────────
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { getOneContract, renewContract, uploadContractDocument } from "../../../api/contracts";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { Modal } from "../../../components/ui/Modal";
import { cn } from "../../../utils/cn";
import { showToast } from "../../../utils/showToast";
import { FileDrop } from "../settings/sections/invoicing/WizardControls";
import { hasPdfSignature, sha256Hex } from "./contractDocumentFile";
import {
  RECONCILE_RETRY_MS,
  formatFileSize,
  hasMatchingSignedDocument,
  isAmbiguousRenewFailure,
  localTodayYmd,
  operatorIdOf,
  pdfFileProblem,
  renewalDefaults,
  renewErrorKey,
  safePdfFileName,
  validateRenewalForm,
} from "./contractRenewal";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ¿La renovación enviada ya está en el contrato? Lo relee; una lectura fallida cuenta como no.
async function renewalSaved(contractId, expected) {
  const fresh = await getOneContract(contractId);
  return !fresh?.error && hasMatchingSignedDocument(fresh, expected);
}

const PHASE_TEXT = {
  verifying: "Verificando el PDF…",
  uploading: "Subiendo el PDF firmado…",
  renewing: "Registrando la renovación…",
};

// Orden de foco ante un envío inválido: el primero con error.
const FIELD_ORDER = ["dateSigned", "startDate", "endDate"];

// Campo etiquetado (label + control + error), con el id del error para aria-describedby.
function Field({ label, htmlFor, error, errorId, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-fg">
        {label}
      </label>
      {children}
      {error && (
        <span id={errorId} className="text-xs text-status-error">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * @param {{ open: boolean, onClose: () => void, contract: object,
 *           onRenewed: () => (void|Promise<void>) }} props
 *   `onRenewed` cierra el modal y refresca el detalle: nunca con la respuesta de la
 *   renovación, que no trae el contrato poblado.
 */
export default function RenewContractModal({ open, onClose, contract, onRenewed }) {
  const { t } = useTranslation();
  const formId = useId();
  const fileLabelId = useId();
  const fileErrorId = useId();
  const fileInfoId = useId();
  const signedId = useId();
  const startId = useId();
  const endId = useId();
  const validityLabelId = useId();
  const reactivateId = useId();
  const errorIds = { dateSigned: useId(), startDate: useId(), endDate: useId() };

  const [values, setValues] = useState(() => renewalDefaults(contract));
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [checkingFile, setCheckingFile] = useState(false);
  const [formError, setFormError] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | verifying | uploading | renewing

  // Última subida a S3 ({ sha256, url, key }): un reintento con el mismo archivo no lo
  // vuelve a subir (cada subida fallida deja un objeto huérfano).
  const uploadedRef = useRef(null);
  const pickSeq = useRef(0);
  const dropRef = useRef(null);
  const inputRefs = { dateSigned: useRef(null), startDate: useRef(null), endDate: useRef(null) };

  const submitting = phase !== "idle";
  const today = localTodayYmd();
  const showReactivate = contract?.status !== "active";

  const close = () => {
    if (!submitting) onClose();
  };

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  // Validación al salir del campo, solo de ese campo (el envío revalida todo).
  const blurField = (name) => {
    const next = validateRenewalForm(values, today)[name];
    setErrors((prev) => (prev[name] === next ? prev : { ...prev, [name]: next }));
  };

  const toggleNoEnd = (checked) => {
    setValues((prev) => ({ ...prev, noEnd: checked }));
    if (checked) setErrors((prev) => ({ ...prev, endDate: undefined }));
  };

  // Tipo y tamaño al instante; la firma `%PDF-` leyendo los primeros bytes. Solo escribe
  // el último archivo elegido. `checkingFile` sigue solo a esa última elección: si falla por
  // tipo o tamaño se apaga acá, y una revisión superada no lo toca porque la vigente lo apaga
  // al terminar (sin esto, elegir un archivo inválido mientras se revisaba otro lo dejaba
  // prendido y bloqueaba el envío).
  const pickFile = async (picked) => {
    const seq = ++pickSeq.current;
    setFormError(null);
    const problem = pdfFileProblem(picked);
    if (problem) {
      setCheckingFile(false);
      setFile(null);
      setFileError(problem);
      return;
    }
    setCheckingFile(true);
    let verdict;
    try {
      verdict = (await hasPdfSignature(picked)) ? null : "El archivo no es un PDF válido.";
    } catch {
      verdict = "No se pudo leer el archivo. Vuelve a elegirlo.";
    }
    if (seq !== pickSeq.current) return;
    setCheckingFile(false);
    setFile(verdict ? null : picked);
    setFileError(verdict);
  };

  const focusField = (name) => {
    if (name === "file") {
      dropRef.current?.querySelector('[role="button"]')?.focus();
      return;
    }
    inputRefs[name]?.current?.focus();
  };

  const finish = async (deduplicated) => {
    showToast(
      deduplicated
        ? t("Ese contrato firmado ya estaba registrado con estas fechas.")
        : t("Contrato renovado: la nueva vigencia ya está registrada."),
      true,
    );
    uploadedRef.current = null;
    await onRenewed();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting || checkingFile) return;

    const fieldErrors = validateRenewalForm(values, today);
    const fileProblem = file ? null : fileError ?? "Adjunta el contrato firmado en PDF.";
    setErrors(fieldErrors);
    setFileError(fileProblem);
    const firstInvalid = fileProblem ? "file" : FIELD_ORDER.find((name) => fieldErrors[name]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }
    const operatorId = operatorIdOf(contract);
    if (!operatorId) {
      setFormError("No se pudo identificar el operador del contrato.");
      return;
    }

    setFormError(null);
    const dates = {
      dateSigned: values.dateSigned,
      startDate: values.startDate,
      endDate: values.noEnd ? null : values.endDate,
    };
    // Inicio del envío: la conciliación solo acepta una entrada guardada después.
    const submittedAt = Date.now();
    try {
      setPhase("verifying");
      let sha256;
      try {
        sha256 = await sha256Hex(file);
      } catch {
        setFormError("No se pudo verificar el archivo en este navegador.");
        return;
      }

      let uploaded = uploadedRef.current?.sha256 === sha256 ? uploadedRef.current : null;
      if (!uploaded) {
        setPhase("uploading");
        try {
          const { url, key } = await uploadContractDocument(file, {
            operatorId,
            contractId: contract._id,
          });
          uploaded = { sha256, url, key };
          uploadedRef.current = uploaded;
        } catch {
          setFormError("No se pudo subir el PDF firmado. Revisa tu conexión y vuelve a intentarlo.");
          return;
        }
      }

      setPhase("renewing");
      const res = await renewContract(contract._id, {
        ...dates,
        reactivate: showReactivate && values.reactivate,
        document: {
          url: uploaded.url,
          key: uploaded.key,
          fileName: safePdfFileName(file.name),
          size: file.size,
          sha256,
        },
      });
      if (!res?.error) {
        await finish(res?.deduplicated === true);
        return;
      }
      if (isAmbiguousRenewFailure(res)) {
        const expected = { sha256, ...dates, submittedAt };
        let saved = await renewalSaved(contract._id, expected);
        if (!saved) {
          // El proxy pudo cortar mientras contracts todavía escribía: una relectura más antes
          // de dar error. La fase sigue en "renewing", así que el texto de progreso no se va.
          await wait(RECONCILE_RETRY_MS);
          saved = await renewalSaved(contract._id, expected);
        }
        if (saved) {
          await finish(false);
          return;
        }
        setFormError(
          "No se pudo confirmar la renovación por un problema de conexión. Vuelve a intentarlo: si ya se había guardado, no se duplica.",
        );
        return;
      }
      setFormError(renewErrorKey(res));
    } finally {
      setPhase("idle");
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title={t("Renovar contrato")}
      description={contract?.companyId?.name || undefined}
      footer={
        <>
          {/* Progreso del envío junto al botón: una subida de 10 MB puede tardar. */}
          <span role="status" aria-live="polite" className="mr-auto text-xs text-fg-muted">
            {PHASE_TEXT[phase] ? t(PHASE_TEXT[phase]) : ""}
          </span>
          <Button variant="ghost" onClick={close} disabled={submitting}>
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={submitting}
            disabled={checkingFile}
            iconLeft={<RefreshCw className="w-4 h-4" aria-hidden="true" />}
          >
            {t("Renovar contrato")}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">
        {formError && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-status-error/30 bg-status-error-soft px-3 py-2.5 text-sm text-status-error"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{t(formError)}</span>
          </div>
        )}

        {/* PDF firmado */}
        <div className="flex flex-col gap-1.5">
          <span id={fileLabelId} className="text-sm font-medium text-fg">
            {t("Contrato firmado (PDF)")}
          </span>
          {/* FileDrop no recibe props de accesibilidad: el grupo le da nombre y descripción, y
              el foco visible del teclado sale del token de marca. */}
          <div
            ref={dropRef}
            role="group"
            aria-labelledby={fileLabelId}
            aria-describedby={fileError ? fileErrorId : file ? fileInfoId : undefined}
            className={cn(
              "rounded-xl [&>[role=button]:focus-visible]:outline-none [&>[role=button]:focus-visible]:ring-2 [&>[role=button]:focus-visible]:ring-brand-500 [&>[role=button]:focus-visible]:ring-offset-2",
              // Con error, el borde de la zona también lo marca (no solo el texto de abajo).
              fileError && "ring-1 ring-status-error",
            )}
          >
            <FileDrop
              accept="application/pdf,.pdf"
              file={file}
              onSelect={pickFile}
              hint={t("PDF firmado, de hasta 10 MB.")}
              disabled={submitting}
            />
          </div>
          {checkingFile && (
            <span className="text-xs text-fg-muted" role="status">
              {t("Revisando el archivo…")}
            </span>
          )}
          {file && !fileError && (
            <span id={fileInfoId} className="text-xs text-fg-muted tabular-nums">
              {formatFileSize(file.size)}
            </span>
          )}
          {fileError && (
            <span id={fileErrorId} role="alert" className="text-xs text-status-error">
              {t(fileError)}
            </span>
          )}
        </div>

        {/* Firma */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("Fecha de firma")}
            htmlFor={signedId}
            error={errors.dateSigned && t(errors.dateSigned)}
            errorId={errorIds.dateSigned}
          >
            <Input
              ref={inputRefs.dateSigned}
              id={signedId}
              type="date"
              value={values.dateSigned}
              max={today}
              onChange={(e) => setField("dateSigned", e.target.value)}
              onBlur={() => blurField("dateSigned")}
              invalid={!!errors.dateSigned}
              aria-invalid={!!errors.dateSigned}
              aria-describedby={errors.dateSigned ? errorIds.dateSigned : undefined}
              disabled={submitting}
              required
            />
          </Field>
        </div>

        {/* Vigencia del documento. Grupo con role y no <fieldset>: el reboot de Bootstrap,
            que la app carga global, hace flotar el <legend>. */}
        <div role="group" aria-labelledby={validityLabelId} className="space-y-3">
          <p id={validityLabelId} className="text-sm font-semibold text-fg">
            {t("Vigencia del contrato firmado")}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label={t("Desde")}
              htmlFor={startId}
              error={errors.startDate && t(errors.startDate)}
              errorId={errorIds.startDate}
            >
              <Input
                ref={inputRefs.startDate}
                id={startId}
                type="date"
                value={values.startDate}
                onChange={(e) => setField("startDate", e.target.value)}
                onBlur={() => blurField("startDate")}
                invalid={!!errors.startDate}
                aria-invalid={!!errors.startDate}
                aria-describedby={errors.startDate ? errorIds.startDate : undefined}
                disabled={submitting}
                required
              />
            </Field>
            <Field
              label={t("Hasta")}
              htmlFor={endId}
              error={errors.endDate && t(errors.endDate)}
              errorId={errorIds.endDate}
            >
              <Input
                ref={inputRefs.endDate}
                id={endId}
                type="date"
                value={values.noEnd ? "" : values.endDate}
                min={values.startDate || undefined}
                onChange={(e) => setField("endDate", e.target.value)}
                onBlur={() => blurField("endDate")}
                invalid={!!errors.endDate}
                aria-invalid={!!errors.endDate}
                aria-describedby={errors.endDate ? errorIds.endDate : undefined}
                disabled={submitting || values.noEnd}
                required={!values.noEnd}
              />
            </Field>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              checked={values.noEnd}
              onChange={(e) => toggleNoEnd(e.target.checked)}
              disabled={submitting}
              className="h-4 w-4 accent-brand-500"
            />
            {t("Sin fecha de término")}
          </label>
          <p className="text-xs text-fg-muted">
            {t(
              "Si la nueva vigencia empieza después del inicio actual, el contrato conserva su fecha de inicio. La vigencia anterior queda en el historial de contratos firmados.",
            )}
          </p>
        </div>

        {/* Reactivación: solo si el contrato no está activo, marcada por defecto. */}
        {showReactivate && (
          <div className="flex items-start gap-2.5 rounded-lg border border-status-info/30 bg-status-info-soft px-3 py-2.5">
            <input
              id={reactivateId}
              type="checkbox"
              checked={values.reactivate}
              onChange={(e) => setField("reactivate", e.target.checked)}
              disabled={submitting}
              className="mt-0.5 h-4 w-4 shrink-0 accent-brand-500"
            />
            <label htmlFor={reactivateId} className="cursor-pointer text-sm text-fg">
              <span className="font-medium">{t("Reactivar contrato")}</span>
              <span className="mt-0.5 block text-xs text-fg-muted">
                {t("Hoy no está activo. Si lo reactivas, vuelve a quedar activo con la nueva vigencia.")}
              </span>
            </label>
          </div>
        )}
      </form>
    </Modal>
  );
}
